import { useMemo } from "react";
import { Alert, Col, DatePicker, Input, Radio, RadioChangeEvent, Row, Select, Space, Table } from "antd";
import moment from "moment";

import FormLayout from "../../FormLayout/FormLayout";
import { ContractAPI } from "../../../Api/ContractAPI";
import { InvestorAPI } from "../../../Api/InvestorAPI";
import { SuitabilityTestAPI } from "../../../Api/SuitabilityTestAPI";
import { SuitabilityTestPT as SuitabilityTestPTModel } from "../../../Models";
import { useSuitabilityTestPT } from "../../../Hooks/useSuitabilityTestPT/useSuitabilityTestPT";
import { ISuitabilityTest } from "./ISuitabilityTest";
import "./SuitabilityTest.scss";

const { Option } = Select;

const SuitabilityTestPT = ({
	baseURL,
	baseURLExternal,
	translations,
	clientId,
	unlockNextStep,
	isReadOnly = false
}: ISuitabilityTest) => {
	const suitabilityTestAPI = useMemo(() => SuitabilityTestAPI(baseURL, clientId), [baseURL, clientId]);
	const investorAPI = useMemo(() => InvestorAPI(baseURL, baseURLExternal, clientId), [baseURL, baseURLExternal, clientId]);
	const contractAPI = useMemo(() => ContractAPI(baseURL, baseURLExternal, clientId), [baseURL, baseURLExternal, clientId]);
	const {
		t,
		contractId,
		investorInfo,
		testData,
		isSuitabilityDone,
		instrumentRows,
		knowledgeQuestions,
		updateField,
		saveTest
	} = useSuitabilityTestPT({
		suitabilityTestAPI,
		contractAPI,
		investorAPI,
		translations,
		unlockNextStep
	});

	return (
		<Row className="suitability-test">
			<FormLayout
				translations={translations}
				loading={false}
				title={translations.suitabilityTest}
				subTitle={t("suitabilityPtSubtitle", "Perfil do investidor PT")}
				onClick={saveTest}
				disabledSaveButton={isSuitabilityDone}
				contractId={contractId}
				nextStep={unlockNextStep}
				enableNextStep={isSuitabilityDone}
				forceEnableNextStep={isReadOnly}
				hideSaveButton={isReadOnly}
			>
				{isSuitabilityDone && (
					<Col span={10}>
						<Alert message={t("suitabilityPtCompletedMessage", "Test completado correctamente")} type="success" showIcon />
					</Col>
				)}

				<Col span={24}>
					<Row gutter={[16, 16]} className="test">
						<Col span="6" className="titles">
							<label className="required">{translations.nombre}</label>
							<Input value={investorInfo.name!} disabled />
						</Col>
						<Col span="6" className="titles">
							<label className="required">{translations.firstLastnameSignatory}</label>
							<Input value={investorInfo.surname!} disabled />
						</Col>
						<Col span="6" className="titles">
							<label>{translations.secondLastnameSignatory}</label>
							<Input value={investorInfo.secondLastName} disabled />
						</Col>
						<Col span="6" className="titles">
							<label className="required">{translations.dateOfCompletion || "Fecha de cumplimentación"}</label>
							<DatePicker
								value={testData.dateOfCompletion ? moment(testData.dateOfCompletion) : undefined}
								format="DD/MM/YYYY"
								disabled={isSuitabilityDone || isReadOnly}
								onChange={(date) => updateField("dateOfCompletion", date ? date.format("YYYY-MM-DD") : null)}
								status={!testData.dateOfCompletion && !isSuitabilityDone ? "error" : ""}
							/>
						</Col>
						<Col span="6" className="titles">
							<label className="required">{translations.birthDate}</label>
							<DatePicker value={investorInfo.birthDate ? moment(investorInfo.birthDate) : undefined} format="DD/MM/YYYY" disabled />
						</Col>
						<Col span="6" className="titles">
							<label className="required">{translations.documentNumber}</label>
							<Input value={investorInfo.dni} disabled />
						</Col>
					</Row>
					<Row gutter={[16, 16]} className="info">
						<strong>{`I. ${t("suitabilityPtKnowledgeExperience", "Conhecimento e Experiência")}`}</strong>
					</Row>
					<Row gutter={[16, 16]} className="test">
						<Col span="24" className="titles">
							<strong>{`1. ${t("suitabilityPtQuestionMarketFollow", "Com que frequência acompanha os desenvolvimentos nos mercados financeiros?")}`}</strong>
							<Radio.Group value={testData.marketFollowFrequency} onChange={(e: RadioChangeEvent) => updateField("marketFollowFrequency", e.target.value)} disabled={isSuitabilityDone || isReadOnly}>
								<Space direction="vertical">
									<Radio value={1}>{t("suitabilityPtQuestionMarketFollowOption1", "Nunca")}</Radio>
									<Radio value={2}>{t("suitabilityPtQuestionMarketFollowOption2", "Raramente")}</Radio>
									<Radio value={3}>{t("suitabilityPtQuestionMarketFollowOption3", "Às vezes")}</Radio>
									<Radio value={4}>{t("suitabilityPtQuestionMarketFollowOption4", "Regularmente")}</Radio>
								</Space>
							</Radio.Group>
						</Col>

						<Col span="24" className="titles">
							<strong>{`2. ${t("suitabilityPtQuestionEducation", "Assinale o seu nível de habilitações académicas")}`}</strong>
							<Radio.Group value={testData.educationLevel} onChange={(e: RadioChangeEvent) => updateField("educationLevel", e.target.value)} disabled={isSuitabilityDone || isReadOnly}>
								<Space direction="vertical">
									<Radio value={1}>{t("suitabilityPtQuestionEducationOption1", "Ensino básico ou inferior")}</Radio>
									<Radio value={2}>{t("suitabilityPtQuestionEducationOption2", "Ensino secundário")}</Radio>
									<Radio value={3}>{t("suitabilityPtQuestionEducationOption3", "Ensino médio / licenciatura / pós-graduação / mestrado / doutoramento (outras especializações)")}</Radio>
									<Radio value={4}>{t("suitabilityPtQuestionEducationOption4", "Licenciatura / pós-graduação / mestrado / doutoramento (finanças, economia ou gestão)")}</Radio>
								</Space>
							</Radio.Group>
						</Col>

						<Col span="24" className="titles">
							<strong>{`3. ${t("suitabilityPtQuestionFinancialProfession", "Exerce ou já exerceu profissão que exija conhecimentos financeiros?")}`}</strong>
							<Radio.Group value={testData.financialProfession} onChange={(e: RadioChangeEvent) => updateField("financialProfession", e.target.value)} disabled={isSuitabilityDone || isReadOnly}>
								<Space direction="vertical">
									<Radio value={1}>{t("suitabilityPtQuestionFinancialProfessionOption1", "Não exerço nem nunca exerci")}</Radio>
									<Radio value={2}>{t("suitabilityPtQuestionFinancialProfessionOption2", "Sim, no passado")}</Radio>
									<Radio value={3}>{t("suitabilityPtQuestionFinancialProfessionOption3", "Sim, atualmente, com alguns conhecimentos")}</Radio>
									<Radio value={4}>{t("suitabilityPtQuestionFinancialProfessionOption4", "Sim, atualmente, com conhecimentos aprofundados")}</Radio>
								</Space>
							</Radio.Group>
						</Col>

						<Col span="24" className="titles">
							<strong>{`4. ${t("suitabilityPtQuestionFinancialServices", "A que tipo de serviços financeiros recorre?")}`}</strong>
							<Radio.Group value={testData.financialServicesUsage} onChange={(e: RadioChangeEvent) => updateField("financialServicesUsage", e.target.value)} disabled={isSuitabilityDone || isReadOnly}>
								<Space direction="vertical">
									<Radio value={1}>{t("suitabilityPtQuestionFinancialServicesOption1", "Só depósitos")}</Radio>
									<Radio value={2}>{t("suitabilityPtQuestionFinancialServicesOption2", "Gestão discricionária")}</Radio>
									<Radio value={3}>{t("suitabilityPtQuestionFinancialServicesOption3", "Contrato de consultoria")}</Radio>
									<Radio value={4}>{t("suitabilityPtQuestionFinancialServicesOption4", "Ordens em mercados / subscrição em fundos")}</Radio>
								</Space>
							</Radio.Group>
						</Col>

						<Col span="24" className="titles">
							<strong>{`5. ${t("suitabilityPtTransactionsSection", "Natureza, volume e frequência de transações")}`}</strong>
							<Table
								className="table suitability-pt-table"
								pagination={false}
								rowKey="key"
								dataSource={instrumentRows}
							>
								<Table.Column
									title={t("suitabilityPtInstrumentColumn", "Instrumento")}
									dataIndex="label"
									key="label"
									width="24%"
								/>
								<Table.Column
									title={t("suitabilityPtHasTradedColumn", "Transacionou?")}
									key="hasTraded"
									width="18%"
									render={(_: any, record: { key: string }) => (
										<Radio.Group
											value={testData[`${record.key}HasTraded`]}
											onChange={(e) => {
												updateField(`${record.key}HasTraded` as keyof SuitabilityTestPTModel, e.target.value);
												if (e.target.value === false) {
													updateField(`${record.key}FrequencyBand` as keyof SuitabilityTestPTModel, null);
													updateField(`${record.key}AmountBand` as keyof SuitabilityTestPTModel, null);
												}
											}}
											disabled={isSuitabilityDone || isReadOnly}
										>
											<Radio value={true}>{t("si", "Sí")}</Radio>
											<Radio value={false}>{t("no", "No")}</Radio>
										</Radio.Group>
									)}
								/>
								<Table.Column
									title={t("suitabilityPtFrequencyColumn", "Frequência")}
									key="frequency"
									width="29%"
									render={(_: any, record: { key: string }) => (
										<Select
											style={{ width: "100%" }}
											value={testData[`${record.key}FrequencyBand`]}
											onChange={(value) => updateField(`${record.key}FrequencyBand` as keyof SuitabilityTestPTModel, value)}
											disabled={isSuitabilityDone || isReadOnly || testData[`${record.key}HasTraded`] !== true}
										>
											<Option value={1}>{t("suitabilityPtFrequencyOption1", "Máximo de 5")}</Option>
											<Option value={2}>{t("suitabilityPtFrequencyOption2", "Entre 5 e 20")}</Option>
											<Option value={3}>{t("suitabilityPtFrequencyOption3", "Entre 20 e 40")}</Option>
											<Option value={4}>{t("suitabilityPtFrequencyOption4", "Mais do que 40")}</Option>
										</Select>
									)}
								/>
								<Table.Column
									title={t("suitabilityPtAmountColumn", "Montante")}
									key="amount"
									width="29%"
									render={(_: any, record: { key: string }) => (
										<Select
											style={{ width: "100%" }}
											value={testData[`${record.key}AmountBand`]}
											onChange={(value) => updateField(`${record.key}AmountBand` as keyof SuitabilityTestPTModel, value)}
											disabled={isSuitabilityDone || isReadOnly || testData[`${record.key}HasTraded`] !== true}
										>
											<Option value={1}>{t("suitabilityPtAmountOption1", "Menos do que 5")}</Option>
											<Option value={2}>{t("suitabilityPtAmountOption2", "Entre 5 e 50")}</Option>
											<Option value={3}>{t("suitabilityPtAmountOption3", "Entre 50 e 100")}</Option>
											<Option value={4}>{t("suitabilityPtAmountOption4", "Mais do que 100")}</Option>
										</Select>
									)}
								/>
							</Table>
						</Col>

						<Col span="24" className="titles">
							<strong>{` II. ${t("suitabilityPtKnowledgeSectionTitle", "Conhecimentos")}`}</strong>
						</Col>

						{knowledgeQuestions.map((question) => (
							<Col span="24" className="titles" key={question.key}>
								<strong>{`${Number(question.key.replace("knowledgeQ", "")) + 5}. ${question.title}`}</strong>
								<Radio.Group
									value={testData[question.key]}
									onChange={(e: RadioChangeEvent) => updateField(question.key as keyof SuitabilityTestPTModel, e.target.value)}
									disabled={isSuitabilityDone || isReadOnly}
								>
									<Space direction="vertical">
										{question.options.map((option) => (
											<Radio key={option.value} value={option.value}>{option.label}</Radio>
										))}
									</Space>
								</Radio.Group>
							</Col>
						))}
					</Row>
				</Col>
			</FormLayout>
		</Row>
	);
};

export default SuitabilityTestPT;
